import { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";
import uploadFill from "@iconify/icons-eva/upload-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import { issueImgUpload } from "src/components/ServiceRequest/serviceRequest.hook";
import { useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { getChargerUploadQRUrl } from "src/react-query/endPoints";

const getQREntries = (qrCodeUrl) => {
  if (!qrCodeUrl) return [];
  try {
    const obj = typeof qrCodeUrl === "string" ? JSON.parse(qrCodeUrl) : qrCodeUrl;
    return Object.entries(obj || {}).filter(([, url]) => !!url);
  } catch {
    return [];
  }
};

const QRPreviewDialog = ({ entry, onClose }) => (
  <Dialog open={!!entry} onClose={onClose} maxWidth="xs">
    <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
      <Typography variant="subtitle1" sx={{ textTransform: "capitalize" }}>
        {entry?.gateway} QR
      </Typography>
      <IconButton size="small" onClick={onClose}>
        <Icon icon={closeFill} />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, pb: 3 }}>
      {entry && (
        <Box
          component="img"
          src={entry.url}
          alt={entry.gateway}
          sx={{ width: 240, height: 240, objectFit: "contain" }}
        />
      )}
    </DialogContent>
  </Dialog>
);

export const UploadQRModal = ({ open, onClose, row, onSaveSuccess }) => {
  const { teamId } = useSelectedTeam();
  const [connectorFiles, setConnectorFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewQR, setPreviewQR] = useState(null);
  const { mutate: saveQR, isLoading: isSaving } = useMutate("chargers");

  const connectors = row?.connectors || [];
  const chargerId = row?.chargepoint_pk;

  const handleFileSelect = useCallback((connectorPk, file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setConnectorFiles((prev) => ({ ...prev, [connectorPk]: { file, previewUrl } }));
  }, []);

  const handleRemove = useCallback((connectorPk) => {
    setConnectorFiles((prev) => {
      const next = { ...prev };
      if (next[connectorPk]?.previewUrl) URL.revokeObjectURL(next[connectorPk].previewUrl);
      delete next[connectorPk];
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    Object.values(connectorFiles).forEach((v) => {
      if (v.previewUrl) URL.revokeObjectURL(v.previewUrl);
    });
    setConnectorFiles({});
    onClose();
  }, [connectorFiles, onClose]);

  const handleSave = async () => {
    const entries = Object.entries(connectorFiles).filter(([, v]) => v.file);
    if (!entries.length) return;

    setUploading(true);
    try {
      const uploaded = await Promise.all(
        entries.map(async ([connectorPk, { file }]) => {
          const response = await issueImgUpload({ image: file, folder: "qr" });
          return {
            connector_pk: Number(connectorPk),
            qr_url: response.data.data.url,
          };
        })
      );

      saveQR(
        {
          url: getChargerUploadQRUrl(teamId, chargerId),
          method: "POST",
          body: { connectors: uploaded },
        },
        {
          onSuccess: () => {
            handleClose();
            onSaveSuccess?.();
          },
        }
      );
    } catch (err) {
      console.error("QR upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const isLoading = uploading || isSaving;
  const hasNewFiles = Object.keys(connectorFiles).length > 0;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6">Upload QR Code</Typography>
          <IconButton onClick={handleClose} size="small" disabled={isLoading}>
            <Icon icon={closeFill} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>{row?.chargepoint_name || row?.chargepoint_id}</strong> — upload a QR code image for each connector.
          </Typography>

          {connectors.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No connectors found for this charger.
            </Typography>
          ) : (
            connectors.map((connector, idx) => {
              const pk = connector.connector_pk;
              const selected = connectorFiles[pk];
              const qrEntries = getQREntries(connector.qrCodeUrl);

              return (
                <Box key={pk ?? idx}>
                  {idx > 0 && <Divider sx={{ my: 2 }} />}

                  {/* Connector header */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2">
                      Connector {connector.connectorId}
                      {connector.connectorName ? ` — ${connector.connectorName}` : ""}
                    </Typography>
                  </Box>

                  {/* Existing QRs row */}
                  {qrEntries.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block" }}>
                        Existing QRs
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        {qrEntries.map(([gateway, url]) => (
                          <Box
                            key={gateway}
                            onClick={() => setPreviewQR({ gateway, url })}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.5,
                              cursor: "pointer",
                              "&:hover .qr-thumb": { borderColor: "primary.main", opacity: 1 },
                            }}
                          >
                            <Box
                              className="qr-thumb"
                              component="img"
                              src={url}
                              alt={gateway}
                              sx={{
                                width: 64,
                                height: 64,
                                objectFit: "contain",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                bgcolor: "grey.50",
                                opacity: 0.8,
                                transition: "border-color 0.15s, opacity 0.15s",
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ textTransform: "capitalize", fontSize: "0.7rem", color: "text.secondary" }}
                            >
                              {gateway}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Upload section */}
                  <Box sx={{ mt: 1.5, display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                          startIcon={<Icon icon={uploadFill} />}
                          disabled={isLoading}
                        >
                          {selected ? "Change" : "Choose File"}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => handleFileSelect(pk, e.target.files[0])}
                          />
                        </Button>
                        {selected && (
                          <IconButton size="small" onClick={() => handleRemove(pk)} disabled={isLoading}>
                            <Icon icon={closeFill} width={16} />
                          </IconButton>
                        )}
                      </Box>

                      {selected && qrEntries.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 1, py: 0.25, fontSize: "0.75rem" }}>
                          A QR is already attached and will be overridden
                        </Alert>
                      )}
                    </Box>

                    {/* New file preview */}
                    {selected?.previewUrl && (
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          border: "1px dashed",
                          borderColor: "primary.main",
                          borderRadius: 1,
                          overflow: "hidden",
                          flexShrink: 0,
                          bgcolor: "grey.50",
                        }}
                      >
                        <Box
                          component="img"
                          src={selected.previewUrl}
                          alt="preview"
                          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSave}
            loading={isLoading}
            disabled={!hasNewFiles}
          >
            Upload & Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <QRPreviewDialog entry={previewQR} onClose={() => setPreviewQR(null)} />
    </>
  );
};
